import { useMemo, useState } from "react";
import Card from "../../../components/core/basic/Card";
import { Flex } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";
import { FaTable } from "react-icons/fa";
import { IoStatsChart } from "react-icons/io5";
import moment from "moment";
import { ButtonGroup } from "../../../components/core/basic/ButtonGroup";
import { trellisPalette } from "../../../components/custom/analytics/palettes";
import { Chart, type ChartOptions } from "@highcharts/react";
import { useClientTable } from "../../../components/table/tableFeatures";
import { Table } from "../../../components/table/Table";
import { formatCurrency, formatPercent, formatNumber } from "../../../utils/formatNumber";
import { createColumnBuilder } from "../../../components/table/columnBuilder";
import { useLiveDashboardFilters } from "../api/useLiveDashboardFilters";
import { useLiveOrdersHourly } from "../api/useLiveOrdersHourly";
import { useLiveOrders } from "../api/useLiveOrders";
import { useCurrentDayAdSpend } from "../api/useCurrentDayAdSpend";
import { useTranslation } from "react-i18next";

type PerformanceRow = {
  metric: string;
  today: number;
  yesterday: number;
  delta: number | null;
  format: (value: number) => string;
};

const columnHelper = createColumnBuilder<PerformanceRow>();

const PerformanceTable = () => {
  const { t } = useTranslation(["live"]);
  const filters = useLiveDashboardFilters();
  const liveOrdersHourlyQuery = useLiveOrdersHourly(filters);
  const liveOrdersQuery = useLiveOrders(filters);
  const currentDayAdSpendQuery = useCurrentDayAdSpend(filters);

  // Current Hour in from UTC to PST (but .tz is not a function in moment)
  const currentHour = Number(moment().utcOffset(-8).format("HH"));

  const hourlyDataToCurrentHour = useMemo(() => {
    if (!liveOrdersHourlyQuery.data) {
      return null;
    }

    const keys = Object.keys(liveOrdersHourlyQuery.data);
    let stop = false;

    const data = {
      yesterday_sales: 0,
      yesterday_units_ordered: 0,
      yesterday_cost: 0,
      yesterday_ad_sales: 0,
      yesterday_impressions: 0,
      yesterday_view_impressions: 0,
      yesterday_clicks: 0,
      yesterday_attributed_conversions_14d: 0,

      today_sales: 0,
      today_units_ordered: 0,
      today_cost: 0,
      today_ad_sales: 0,
      today_impressions: 0,
      today_view_impressions: 0,
      today_clicks: 0,
      today_attributed_conversions_14d: 0,
    };

    for (let i = 0; i < keys.length; i++) {
      if (currentHour > 0 && i === currentHour - 1) {
        stop = true;
      }

      const dayData = liveOrdersHourlyQuery.data[keys[i]!] ?? {};

      data.today_sales += dayData.today_sales || 0;
      data.today_units_ordered += dayData.today_units_ordered || 0;
      data.today_cost += dayData.today_cost || 0;
      data.today_ad_sales += dayData.today_ad_sales || 0;
      data.today_impressions += dayData.today_impressions || 0;
      data.today_view_impressions += dayData.today_view_impressions || 0;
      data.today_clicks += dayData.today_clicks || 0;
      data.today_attributed_conversions_14d += dayData.today_attributed_conversions_14d || 0;

      data.yesterday_sales += dayData.yesterday_sales || 0;
      data.yesterday_units_ordered += dayData.yesterday_units_ordered || 0;
      data.yesterday_cost += dayData.yesterday_cost || 0;
      data.yesterday_ad_sales += dayData.yesterday_ad_sales || 0;
      data.yesterday_impressions += dayData.yesterday_impressions || 0;
      data.yesterday_view_impressions += dayData.yesterday_view_impressions || 0;
      data.yesterday_clicks += dayData.yesterday_clicks || 0;
      data.yesterday_attributed_conversions_14d += dayData.yesterday_attributed_conversions_14d || 0;

      if (stop) {
        break;
      }
    }
    return data;
  }, [liveOrdersHourlyQuery.data, currentHour]);

  const data = useMemo(() => {
    const liveData = liveOrdersQuery.data;
    const liveAdSpendData = currentDayAdSpendQuery.data;

    if (!liveData || !liveAdSpendData || !hourlyDataToCurrentHour) return [];

    return [
      {
        metric: t(($) => $.live["Units Sold"]),
        today: liveData.quantity_ordered,
        yesterday: hourlyDataToCurrentHour.yesterday_units_ordered,
        delta:
          hourlyDataToCurrentHour.yesterday_units_ordered > 0 ?
            (liveData.quantity_ordered - hourlyDataToCurrentHour.yesterday_units_ordered) /
            hourlyDataToCurrentHour.yesterday_units_ordered
          : 0,
        format: formatNumber,
      },
      {
        metric: t(($) => $.live["Total Sales"]),
        today: liveData.order_item_value,
        yesterday: hourlyDataToCurrentHour.yesterday_sales,
        delta:
          hourlyDataToCurrentHour.yesterday_sales > 0 ?
            (liveData.order_item_value - hourlyDataToCurrentHour.yesterday_sales) /
            hourlyDataToCurrentHour.yesterday_sales
          : 0,
        format: formatCurrency,
      },
      {
        metric: t(($) => $.live["Ad Spend"]),
        today: liveAdSpendData.cost,
        yesterday: hourlyDataToCurrentHour.yesterday_cost,
        delta:
          hourlyDataToCurrentHour.yesterday_cost > 0 ?
            (liveAdSpendData.cost - hourlyDataToCurrentHour.yesterday_cost) / hourlyDataToCurrentHour.yesterday_cost
          : 0,
        format: formatCurrency,
      },
      {
        metric: t(($) => $.live["Ad Sales"]),
        today: liveAdSpendData.attributed_sales_7d,
        yesterday: hourlyDataToCurrentHour.yesterday_ad_sales,
        delta:
          hourlyDataToCurrentHour.yesterday_ad_sales > 0 ?
            (liveAdSpendData.attributed_sales_7d - hourlyDataToCurrentHour.yesterday_ad_sales) /
            hourlyDataToCurrentHour.yesterday_ad_sales
          : 0,
        format: formatCurrency,
      },
      {
        metric: t(($) => $.live["Impressions"]),
        today: liveAdSpendData.impressions,
        yesterday: hourlyDataToCurrentHour.yesterday_impressions,
        delta:
          hourlyDataToCurrentHour.yesterday_impressions > 0 ?
            (liveAdSpendData.impressions - hourlyDataToCurrentHour.yesterday_impressions) /
            hourlyDataToCurrentHour.yesterday_impressions
          : 0,
        format: formatNumber,
      },
      {
        metric: t(($) => $.live["Clicks"]),
        today: liveAdSpendData.clicks,
        yesterday: hourlyDataToCurrentHour.yesterday_clicks,
        delta:
          hourlyDataToCurrentHour.yesterday_clicks > 0 ?
            (liveAdSpendData.clicks - hourlyDataToCurrentHour.yesterday_clicks) /
            hourlyDataToCurrentHour.yesterday_clicks
          : 0,
        format: formatNumber,
      },
      {
        metric: t(($) => $.live["ACOS"]),
        today: liveAdSpendData.attributed_sales_7d > 0 ? liveAdSpendData.cost / liveAdSpendData.attributed_sales_7d : 0,
        yesterday:
          hourlyDataToCurrentHour.yesterday_ad_sales > 0 ?
            hourlyDataToCurrentHour.yesterday_cost / hourlyDataToCurrentHour.yesterday_ad_sales
          : 0,
        delta: null,
        format: formatPercent,
      },
      {
        metric: t(($) => $.live["TACOS"]),
        today: liveData.order_item_value > 0 ? liveAdSpendData.cost / liveData.order_item_value : 0,
        yesterday:
          hourlyDataToCurrentHour.yesterday_sales > 0 ?
            hourlyDataToCurrentHour.yesterday_cost / hourlyDataToCurrentHour.yesterday_sales
          : 0,
        delta: null,
        format: formatPercent,
      },
    ];
  }, [hourlyDataToCurrentHour, liveOrdersQuery.data, currentDayAdSpendQuery.data, t]);

  // Define columns for ReactTable

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("metric", {
        header: t(($) => $.live["Metrics"]),
      }),
      columnHelper.accessor("today", {
        header: t(($) => $.live["Today"]),
        cell: (info) => info.row.original.format(info.getValue()),
      }),
      columnHelper.accessor("yesterday", {
        header: t(($) => $.live["Yesterday"]),
        cell: (info) => info.row.original.format(info.getValue()),
      }),
      columnHelper.accessor("delta", {
        header: "%",
        cell: (info) => (info.getValue() !== null ? formatPercent(info.getValue()) : "-"),
      }),
    ];
  }, [t]);

  const table = useClientTable({
    data,
    columns,
    enableSorting: false,
  });

  return (
    <div
      className={css({
        borderRadius: "md",
        width: "100%",
        padding: "small",
        backgroundColor: "white",
      })}
    >
      <Table
        table={table}
        className={css({
          width: "100%",
          borderCollapse: "collapse",
          "& th, td": {
            width: "25%",
            paddingY: "8px",
            textAlign: "center",
          },
        })}
      />
    </div>
  );
};

const PerformanceByHourSection = () => {
  const { t } = useTranslation(["live"]);
  const [selectedTab, setSelectedTab] = useState("chart");
  const [selectedBucket, setSelectedBucket] = useState("units_ordered");

  const filters = useLiveDashboardFilters();
  const liveOrdersHourlyQuery = useLiveOrdersHourly(filters);

  type HourData = Record<string, number | undefined>;
  type LiveOrdersHourlyData = Record<string, HourData>;

  const hourChartOptions = useMemo(() => {
    if (!liveOrdersHourlyQuery.data) {
      return null;
    }

    let todayKey = "today_units_ordered";
    let yesterdayKey = "yesterday_units_ordered";

    if (selectedBucket === "sales") {
      todayKey = "today_sales";
      yesterdayKey = "yesterday_sales";
    } else if (selectedBucket === "impressions") {
      todayKey = "today_impressions";
      yesterdayKey = "yesterday_impressions";
    } else if (selectedBucket === "clicks") {
      todayKey = "today_clicks";
      yesterdayKey = "yesterday_clicks";
    } else if (selectedBucket === "cost") {
      todayKey = "today_cost";
      yesterdayKey = "yesterday_cost";
    } else if (selectedBucket === "ad_sales") {
      todayKey = "today_ad_sales";
      yesterdayKey = "yesterday_ad_sales";
    }
    const hours = Object.keys(liveOrdersHourlyQuery.data);
    const todayUnits = hours.map((hour) => {
      const value = (liveOrdersHourlyQuery.data as LiveOrdersHourlyData)[hour]?.[todayKey];
      return value && value > 0 ? value : 0;
    });
    const yesterdayUnits = hours.map((hour) => {
      const value = (liveOrdersHourlyQuery.data as LiveOrdersHourlyData)[hour]?.[yesterdayKey];
      return value && value > 0 ? value : 0;
    });

    const hourChartOptions: ChartOptions = {
      chart: {
        type: "column",
        style: {
          fontFamily: "Poppins",
          fontWeight: "300",
          fontSize: "15px",
        },
        height: "350px",
      },
      title: {
        text: undefined,
      },
      xAxis: {
        categories: hours,
        title: {
          text: t(($) => $.live["Hour"]),
        },
      },
      yAxis: {
        title: {
          text: undefined,
        },
      },
      legend: {
        align: "right",
        verticalAlign: "top",
        layout: "vertical",
        enabled: false,
      },
      plotOptions: {
        column: {
          pointPadding: 0.15,
          borderWidth: 0,
          groupPadding: 0.15,
        },
      },
      series: [
        {
          name: t(($) => $.live["Today"]),
          data: todayUnits,
          color: trellisPalette[0],
        },
        {
          name: t(($) => $.live["Yesterday"]),
          data: yesterdayUnits,
          color: trellisPalette[2],
        },
      ],
    };

    return hourChartOptions;
  }, [liveOrdersHourlyQuery.data, selectedBucket, t]);

  return (
    <Card>
      <Flex className={css({ justifyContent: "space-between", alignItems: "center", fontSize: "large" })}>
        <span />
        <h5 className={css({ fontSize: "large", fontWeight: "bold" })}>
          {selectedTab === "table" ?
            t(($) => $.live["Performance from Midnight to {time} PST"], {
              time: moment().utcOffset(-9).format("HH:mm"),
            })
          : t(($) => $.live["Performance By Hour (PST)"])}
        </h5>

        <ButtonGroup
          variant="inline"
          buttons={[
            {
              value: "chart",
              label: <IoStatsChart />,
            },
            {
              value: "table",
              label: <FaTable />,
            },
          ]}
          onSelect={(value) => setSelectedTab(value)}
          value={selectedTab}
        />
      </Flex>
      {selectedTab === "chart" && (
        <div>
          <Flex justifyContent={"center"} marginBottom={"large"}>
            <ButtonGroup
              buttons={[
                { label: t(($) => $.live["Units"]), value: "units_ordered" },
                { label: t(($) => $.live["Sales"]), value: "sales" },
                {
                  label: t(($) => $.live["Imp."]),
                  value: "impressions",
                },
                { label: t(($) => $.live["Clicks"]), value: "clicks" },
                { label: t(($) => $.live["Spend"]), value: "cost" },
                { label: t(($) => $.live["Ad $"]), value: "ad_sales" },
              ]}
              value={selectedBucket}
              onSelect={(values) => {
                setSelectedBucket(values);
              }}
            />
          </Flex>
          {hourChartOptions && <Chart options={hourChartOptions} />}
        </div>
      )}
      {selectedTab === "table" && <PerformanceTable />}
    </Card>
  );
};

export default PerformanceByHourSection;
